import { useEffect, useRef } from 'react'
import { Navigate, Route, Routes, useParams, useLocation } from 'react-router-dom'
import Login from './pages/Login.jsx'
import Signup from './pages/Signup.jsx'
import Order from './pages/Order.jsx'
import Confirmation from './pages/Confirmation.jsx'
import Account from './pages/Account.jsx'
import ResetPassword from './pages/ResetPassword.jsx'
import Review from './pages/Review.jsx'
import InstallPage from './pages/InstallPage.jsx'
import ZipEntry from './pages/ZipEntry.jsx'
import MyOrders from './pages/MyOrders.jsx'
import OrderDetail from './pages/OrderDetail.jsx'
import MembersSuccess from './pages/MembersSuccess.jsx'
import MembersCanceled from './pages/MembersCanceled.jsx'
import Admin from './pages/Admin.jsx'
import StaffNewOrder from './pages/StaffNewOrder.jsx'
// AdminOrderDetail replaced by the OrderPanel side drawer inside Admin.jsx.
import Toast from './components/Toast.jsx'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import { useAuth } from './context/AuthContext.jsx'
import { initAdTracking, trackPageView } from './lib/adTracking'

// Loads the ad tags once, after the first paint, then reports SPA route
// changes. Mounted inside <Routes>'s parent so it covers EVERY page —
// including the ones behind the login, which is what fills the Google
// remarketing lists.
function TrackingLayer() {
  const { pathname, search } = useLocation()
  const first = useRef(true)

  useEffect(() => { initAdTracking() }, [])

  useEffect(() => {
    // The bootstrap already fired the first view; firing it again here
    // would double-count every session's landing page.
    if (first.current) { first.current = false; return }
    trackPageView(pathname + (search || ''))
  }, [pathname, search])

  return null
}

function Splash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="text-[13px] font-bold text-ink-2">Loading…</div>
    </div>
  )
}

function Guard({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <Splash />
  return user ? children : <Navigate to="/login" replace />
}

// Preserve the deep-link param when redirecting /admin/orders/:n → /staff/orders/:n.
function StaffOrderRedirect() {
  const { orderNumber } = useParams()
  return <Navigate to={`/staff/orders/${orderNumber}`} replace />
}

function RootRedirect() {
  const { user, loading } = useAuth()
  if (loading) return <Splash />
  // Logged-out visitors land on the ZIP screen — spec §1. Existing
  // customers reach /login from a link on that screen.
  return <Navigate to={user ? '/order' : '/entry'} replace />
}

export default function App() {
  return (
    <>
      <TrackingLayer />
      <ErrorBoundary>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/entry" element={<ZipEntry />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Public — the token in the delivered email is the credential. */}
        <Route path="/review" element={<Review />} />
        {/* Public on purpose: this is the link Sébastien texts to someone
            who isn't a customer yet. Behind <Guard> it would be useless. */}
        <Route path="/install" element={<InstallPage />} />
        <Route path="/order" element={<Guard><Order /></Guard>} />
        <Route path="/my-orders" element={<Guard><MyOrders /></Guard>} />
        <Route path="/orders/:orderNumber" element={<Guard><OrderDetail /></Guard>} />
        <Route path="/confirmation" element={<Guard><Confirmation /></Guard>} />
        <Route path="/account" element={<Guard><Account /></Guard>} />
        <Route path="/members/success" element={<Guard><MembersSuccess /></Guard>} />
        <Route path="/members/canceled" element={<Guard><MembersCanceled /></Guard>} />
        {/* /members/prepaid/* removed 2026-10-08 with the prepaid sale. Old
            links land on the catch-all below and go to '/', never a blank
            screen. */}
        {/* Legacy /profile still works, redirected to /account. */}
        <Route path="/profile" element={<Navigate to="/account" replace />} />

        {/* Staff / admin — self-guards inside Admin.jsx (auth + admins
            table check). Sébastien bookmarks /staff; /admin is kept alive
            as a client-side redirect so old bookmarks + links keep working. */}
        <Route path="/staff" element={<Admin />} />
        <Route path="/staff/orders/new" element={<StaffNewOrder />} />
        <Route path="/staff/orders/:orderNumber" element={<Admin />} />
        <Route path="/admin" element={<Navigate to="/staff" replace />} />
        <Route path="/admin/orders/:orderNumber" element={<StaffOrderRedirect />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
      <Toast />
    </>
  )
}
